import React from 'react';
import styled from 'styled-components';

import ClickEventLoggerWrapper from '@/components/common/ClickEventLogWrapper';
import InfiniteScroll from '@/components/common/infinite-scroll';
import LinkTag from '@/components/common/link-tag';
import HospitalListItem from '@/components/hospital/hospital-list-item/HospitalListItem';
import HospitalSkeletonList from '@/components/templates/f2f/common/hospital-list/hospital-skeleton-list';
import { TotalSearchHospitalViewModel } from '@/hooks/search/hospital/useSearchHospitalViewModel';
import { ROUTES } from '@/types/common';
import { ContractType } from '@/types/hospital';
import ResultSection from '../../result-section';
import EmptySearchResult from '../empty-search-result';

interface Props {
  hospitalViewModel: TotalSearchHospitalViewModel;
  address: string;
  geolocationPermissionLoading: boolean;
  getCurrentLocation: () => void;
}
const HospitalSearchResult = ({
  hospitalViewModel,
  address,
  geolocationPermissionLoading,
  getCurrentLocation,
}: Props) => {
  if (hospitalViewModel.isHospitalLoading || geolocationPermissionLoading) {
    return <HospitalSkeletonList />;
  }

  if (!hospitalViewModel.hospitalData?.pages.length) {
    return <EmptySearchResult handleClickRequestButton={hospitalViewModel.handleClickRequestButton} />;
  }
  return (
    <>
      <ResultSection.ResultAddress address={address} getCurrentLocation={getCurrentLocation} marginTop={0} />

      <InfiniteScroll
        isLoading={hospitalViewModel.isHospitalLoading}
        hasNextPage={hospitalViewModel.hasHospitalNextPage}
        fetchNextPage={hospitalViewModel.fetchHospitalNextPage}
      >
        <HospitalResultWrapper>
          {hospitalViewModel.hospitalData?.pages?.map((hospital) => (
            <LinkTag
              key={`hospital_${hospital.hospitalPublicId}`}
              onClickNavigateToScreen={() => hospitalViewModel.onClickMoveNative(hospital.hospitalPublicId)}
              href={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
            >
              <ClickEventLoggerWrapper
                onClickEventLog={() => hospitalViewModel.onClickLogHospital(hospital.hospitalPublicId)}
              >
                <HospitalListItem
                  key={`event-${hospital.hospitalPublicId}`}
                  name={hospital.hospitalName}
                  distance={hospital.hospitalDistance}
                  rating={hospital.reviewScore}
                  reviewTotalCount={hospital.reviewTotalCount}
                  badges={hospital.hospitalBadges}
                  imageUrl={hospital.hospitalRepresentativeImageUrl}
                  address={hospital.hospitalAddress}
                  status={hospital.hospitalOperationStatus}
                  isBookable={hospital.isBookable}
                  representativeDepartmentName={hospital.hospitalRepresentativeDepartmentName}
                  hospitalPid={hospital.hospitalPublicId}
                  doctorDetailPageHref={`${ROUTES.HOSPITAL.DETAIL.path}${hospital.hospitalPublicId}`}
                  onClickMoveToDoctorDetail={() => {
                    hospitalViewModel.onClickMoveNative(hospital.hospitalPublicId);
                  }}
                  hasNonF2fDoctors={!!hospital.doctors.find((doctor) => doctor.contractType === ContractType.All)}
                />
              </ClickEventLoggerWrapper>
            </LinkTag>
          ))}
        </HospitalResultWrapper>
      </InfiniteScroll>
    </>
  );
};

export default HospitalSearchResult;

const HospitalResultWrapper = styled.div`
  > *:not(:last-child) > div {
    border-bottom-style: solid;
    border-bottom-width: 1px;
    border-bottom-color: ${({ theme }) => theme.color.gray40};
  }
`;
